// Brand elements from the SSG Brand Style Guide (Oct 2026).
import type { Icon } from "@phosphor-icons/react";
import {
	Bank,
	Briefcase,
	Buildings,
	CalendarCheck,
	ChartBar,
	ClipboardText,
	Clock,
	CurrencyGbp,
	FileArrowUp,
	GearSix,
	IdentificationBadge,
	Info,
	Lightbulb,
	ListChecks,
	Microphone,
	Quotes,
	RocketLaunch,
	ShieldCheck,
	Target,
	UsersThree,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";

/** The SSG symbol, masked so it takes the current text colour. Decorative unless labelled. */
export function Mark({ size = 36, className = "", label }: { size?: number; className?: string; label?: string }) {
	return (
		<span
			className={`ssg-mark shrink-0 ${className}`}
			style={{ width: size, height: size }}
			{...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
		/>
	);
}

/** The guide's topic badges: one solid symbol in a circle (navy + pink; mint + purple in dark mode). */
export const TOPICS = {
	tips: Info,
	podcast: Microphone,
	testimonial: Quotes,
	launch: RocketLaunch,
	growth: ChartBar,
	goals: Target,
	people: UsersThree,
	finance: CurrencyGbp,
	systems: GearSix,
	ideas: Lightbulb,
	planning: CalendarCheck,
	trust: ShieldCheck,
	// Additions in the same badge style, where the guide's set has no clear symbol.
	clients: Buildings,
	upload: FileArrowUp,
	tasks: ListChecks,
	time: Clock,
	jobs: Briefcase,
	payroll: Bank,
	forms: ClipboardText,
	team: IdentificationBadge,
} satisfies Record<string, Icon>;

/**
 * On a navy block the guide flips it: accent (pink) circle, post-colour (navy) symbol.
 */
export function Badge({
	topic,
	size = 48,
	onNavy = false,
}: {
	topic: keyof typeof TOPICS;
	size?: number;
	onNavy?: boolean;
}) {
	const Glyph = TOPICS[topic];
	const colours = onNavy ? "bg-pink text-navy" : "bg-[var(--color-badge)] text-[var(--color-badge-symbol)]";
	return (
		<span
			aria-hidden="true"
			className={`inline-flex shrink-0 items-center justify-center rounded-full ${colours}`}
			style={{ width: size, height: size }}
		>
			<Glyph weight="fill" size={Math.round(size * 0.55)} />
		</span>
	);
}

/** Small uppercase section label above a title, as on the guide's pages ("01 / IDENTITY"). */
export function Eyebrow({ children }: { children: ReactNode }) {
	return <p className="font-sans text-sm font-bold tracking-[0.04em] text-label uppercase">{children}</p>;
}

/**
 * The template curve: a colour block that ends in a sweeping pink edge.
 * Every point of both curves stays inside the 400 x 64 viewBox (the lowest
 * point of the pink band is y = 53), so nothing is clipped at any width.
 */
export function CurveEdge() {
	return (
		<svg aria-hidden="true" viewBox="0 0 400 64" preserveAspectRatio="none" className="block h-12 w-full">
			<title>Decorative curve</title>
			<path d="M0 0 H400 V14 C290 50 140 52 0 20 Z" fill="var(--color-navy)" />
			<path d="M0 20 C140 52 290 50 400 14 V26 C290 60 140 62 0 30 Z" fill="var(--color-pink)" />
		</svg>
	);
}
